import { useContext, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { AccessPolicyUpdate } from "@/api/admin-api"

import { Pencil, Shield, ShieldCheck } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { AdminFocusedSurfaceContext } from "@/features/admin-dashboard/admin-focused-surface-context"
import { AccessPolicyDialog } from "@/features/admin-dashboard/components/access-policy-dialog"
import { EditAdminGroupDialog } from "@/features/admin-dashboard/components/edit-admin-group-dialog"
import {
  useAdminGroupsQuery,
  useUpdateAdminGroupPolicyMutation,
} from "@/features/admin-dashboard/hooks/admin-queries"
import { isPolicyConfigured } from "@/features/admin-dashboard/utils/access-policy"

type AdminGroupInfo = {
  key: string
  label: string
  description: string
}

const ADMIN_GROUPS: AdminGroupInfo[] = [
  {
    key: "admin_dashboard",
    label: "Platform Admins",
    description:
      "Full admin dashboard access and system configuration (platform staff). " +
      "Members always retain every capability; this policy cannot be saved in a " +
      "state that excludes yourself.",
  },
  {
    key: "customer_admins",
    label: "Customer Admins",
    description:
      "Customer-side administrators. Members see the admin dashboard filtered by " +
      "the delegation matrix configured by platform admins — until capabilities " +
      "are delegated, membership grants nothing.",
  },
  {
    key: "workflow_admins",
    label: "Workflow Admins",
    description: "Manage workflows, prompts, and explorer templates",
  },
  {
    key: "source_admins",
    label: "Source Admins",
    description: "Manage trusted sources and data source configuration",
  },
  {
    key: "prompt_admins",
    label: "Prompt Admins",
    description: "Manage prompt templates and system prompts",
  },
  {
    key: "parser_admins",
    label: "Parser Admins",
    description: "Manage document parser configuration",
  },
  {
    key: "usecase_admins",
    label: "AI Portfolio Admins",
    description: "QA review, AI features, and portfolio administration",
  },
  {
    key: "tender_admins",
    label: "Tender Admins",
    description:
      "Grants access to the opt-in tender admin mode. Members can switch on admin mode to view and edit any tender case; with it off they get the normal involvement-based experience.",
  },
  {
    key: "slide_sense_admins",
    label: "Slide Hub Admins",
    description:
      "Manage Slide Hub folders, decks, slides, and usage analytics. Configured users and groups get standalone Slide Hub admin access; leave empty to allow all Admin Dashboard members.",
  },
  {
    key: "segment_admins",
    label: "Segment Analysis Admins",
    description:
      "Segment Analysis administration: create segments, edit/run any segment, reassign owners. Falls back to admin_dashboard / prompt_admins members when not configured.",
  },
  {
    key: "slide_sense_super_admins",
    label: "Slide Hub Super Admins",
    description:
      "Bulk import of slide decks into Slide Hub. Sits above slide_sense_admins — empty by default, so until configured nobody can bulk-import.",
  },
]

export function AdminGroupsPanel() {
  const { t } = useTranslation()
  const isFocusedSurface = useContext(AdminFocusedSurfaceContext)
  const { data, isLoading } = useAdminGroupsQuery()
  const updateMutation = useUpdateAdminGroupPolicyMutation()

  const [editingGroup, setEditingGroup] = useState<AdminGroupInfo | null>(null)
  const [rawEditingGroup, setRawEditingGroup] = useState<AdminGroupInfo | null>(null)

  const adminGroups = data?.admin_groups ?? {}

  const handleSave = async (policy: AccessPolicyUpdate) => {
    if (!editingGroup) return
    try {
      await updateMutation.mutateAsync({ groupKey: editingGroup.key, policy })
      toast.success(`${editingGroup.label} access policy updated`)
    } catch (error) {
      toast.error("Failed to update access policy", {
        description: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }

  return (
    <>
      <Card className="rounded-lg shadow-sm">
        {!isFocusedSurface ? (
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="size-5" />
              {t("adminDashboard.adminGroups.title", "Admin Groups")}
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              {t(
                "adminDashboard.adminGroups.description",
                "Manage which users and groups have administrative access to different features."
              )}
            </p>
          </CardHeader>
        ) : null}
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-2">
              {ADMIN_GROUPS.map((group) => {
                const policy = adminGroups[group.key]
                const configured = isPolicyConfigured(policy)

                return (
                  <div
                    key={group.key}
                    className="flex flex-col items-start justify-between gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{group.label}</span>
                        {configured ? (
                          <Badge className="gap-1 text-[10px]" variant="default">
                            <ShieldCheck className="size-3" />
                            Configured
                          </Badge>
                        ) : (
                          <Badge className="text-[10px]" variant="secondary">
                            Not configured
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground text-xs">{group.description}</p>
                    </div>
                    <div className="flex w-full items-center justify-end gap-1 sm:w-auto">
                      <Button size="sm" variant="outline" onClick={() => setEditingGroup(group)}>
                        {t("adminDashboard.adminGroups.configure", "Configure")}
                      </Button>
                      <Button
                        aria-label={t("adminDashboard.adminGroups.editRaw", "Edit raw JSON")}
                        size="icon"
                        variant="ghost"
                        onClick={() => setRawEditingGroup(group)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {rawEditingGroup ? (
        <EditAdminGroupDialog
          groupKey={rawEditingGroup.key}
          groupLabel={rawEditingGroup.label}
          isOpen={!!rawEditingGroup}
          onOpenChange={(open) => {
            if (!open) setRawEditingGroup(null)
          }}
          onOpenStructuredEditor={() => {
            setEditingGroup(rawEditingGroup)
            setRawEditingGroup(null)
          }}
        />
      ) : null}

      {editingGroup ? (
        <AccessPolicyDialog
          description={editingGroup.description}
          isSaving={updateMutation.isPending}
          open={!!editingGroup}
          policy={adminGroups[editingGroup.key]}
          title={`${editingGroup.label} Access`}
          onSave={handleSave}
          onOpenChange={(open) => {
            if (!open) setEditingGroup(null)
          }}
        />
      ) : null}
    </>
  )
}
